import { useState } from 'react';
import { ListChecks, RefreshCw } from 'lucide-react';
import { addToSelection, labelOrDefault, matchQualifications, summarisePull } from '../../utils/credentialQualifications';

// "Pull from candidate's <label>" for the Request Credentials pickers: reads the candidate's Qualifications
// (or whatever this client calls the field in Bullhorn: Credentials, Certifications, ...) and TICKS the matching credentials, adding to whatever is already
// ticked. It only ever ticks: nothing is unticked, and nothing is sent until the recruiter sends the request.
// Plain elements, like the other shared embed components, so it also renders under jest.

const TONES = {
  ok: 'text-emerald-700 bg-emerald-50 border-emerald-200',
  info: 'text-slate-600 bg-slate-50 border-slate-200',
  warn: 'text-amber-800 bg-amber-50 border-amber-200',
  error: 'text-red-700 bg-red-50 border-red-200',
};

export default function PullQualificationsButton({
  candidateId,
  categories,
  selectedIds,
  onSelectIds,
  fetchQualifications, // (candidateId) => Promise<{ qualifications, error? }>
  label, // what this client calls the field in Bullhorn (from the categories lookup); our wording if unknown
  testId = 'credreq-pull-qualifications',
}) {
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState(null);
  const listReady = Array.isArray(categories) && categories.length > 0;
  const field = labelOrDefault(label);

  const pull = async () => {
    setBusy(true);
    setNote(null);
    try {
      const data = await fetchQualifications(candidateId);
      if (data?.error) {
        setNote({ tone: 'error', text: `Couldn't read this candidate's ${field} from Bullhorn: ${data.error}` });
        return;
      }
      const qualifications = data?.qualifications || [];
      const result = matchQualifications(qualifications, categories);
      const ticked = new Set((selectedIds || []).map(String));
      const alreadyTicked = result.matchedIds.filter((id) => ticked.has(id)).length;
      if (result.matchedIds.length) onSelectIds(addToSelection(selectedIds, result.matchedIds));
      setNote(summarisePull({ total: qualifications.length, matched: result.matched, unmatched: result.unmatched, alreadyTicked, label }));
    } catch (error) {
      setNote({ tone: 'error', text: error?.response?.data?.detail || `Couldn't read the ${field}. Please try again.` });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-1.5" data-testid={testId}>
      <button
        type="button"
        onClick={pull}
        disabled={busy || !listReady}
        title={listReady ? `Tick the credentials that match this candidate's ${field} in Bullhorn` : 'The credential list is still loading'}
        className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md border border-emerald-500 text-emerald-700 text-xs font-medium hover:bg-emerald-50 disabled:opacity-50 disabled:cursor-not-allowed"
        data-testid={`${testId}-btn`}
      >
        {busy ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <ListChecks className="w-3.5 h-3.5" />}
        {busy ? `Reading ${field}…` : `Pull from candidate's ${field}`}
      </button>
      {note && (
        <p className={`text-xs rounded-md border px-2.5 py-1.5 ${TONES[note.tone] || TONES.info}`} role="status" data-testid={`${testId}-note`}>
          {note.text}
        </p>
      )}
    </div>
  );
}
